import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Mail, MailCheck } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { requestPasswordReset } from "@/api/auth";
import { validateEmail } from "@/lib/validation";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }
    setError(undefined);
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={sent ? "Check your inbox" : "Reset your password"}
      subtitle={
        sent
          ? `If an account exists for ${email}, a reset link is on its way.`
          : "Enter your email and we'll send you a reset link."
      }
    >
      {sent ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="space-y-6"
        >
          <div className="flex flex-col items-center rounded-2xl border border-emerald-200 bg-emerald-50/60 p-8 text-center dark:border-emerald-500/20 dark:bg-emerald-500/10">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-500 text-white">
              <MailCheck size={26} />
            </span>
            <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">
              Didn&apos;t get it? Check spam, or{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="font-semibold text-brand-600 hover:text-brand-500 dark:text-brand-400"
              >
                try another email
              </button>
              .
            </p>
          </div>
          <Button to="/login" variant="outline" className="w-full">
            <ArrowLeft size={18} /> Back to sign in
          </Button>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <Input
            id="email"
            type="email"
            label="Email"
            icon={Mail}
            placeholder="you@company.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(undefined);
            }}
            error={error}
          />
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Sending link…
              </>
            ) : (
              <>
                Send reset link <ArrowRight size={18} />
              </>
            )}
          </Button>

          <Link
            to="/login"
            className="flex items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400"
          >
            <ArrowLeft size={16} /> Back to sign in
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}
